import { Injectable } from '@angular/core';
import confetti from 'canvas-confetti';

/**
 * Configuration options for confetti effects
 * Extends canvas-confetti Options with preset support
 */
export type ConfettiConfig = confetti.Options & {
  preset?: 'win' | 'redeem' | 'celebration' | 'custom';
};

/**
 * SharedConfettiService
 * 
 * Centralized confetti management for all components and games.
 * Provides preset configurations and campaign-configurable colors.
 * 
 * @example
 * ```typescript
 * constructor(private confettiService: ConfettiService) {}
 * 
 * onWin() {
 *   this.confettiService.win(); // Use win preset
 * }
 * 
 * onCustom() {
 *   this.confettiService.trigger({
 *     particleCount: 300,
 *     colors: ['#FFD700', '#FFA500']
 *   });
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ConfettiService {
  /**
   * Campaign-specific colors (can be set from campaign settings)
   * Defaults to colorful celebration palette
   */
  private campaignColors: string[] = [
    '#ff0000', // Red
    '#00ff00', // Green
    '#0000ff', // Blue
    '#FFD700', // Gold
    '#FFA500', // Orange
    '#FF1493'  // Pink
  ];

  /**
   * Set campaign-specific confetti colors
   * @param colors Array of hex color codes
   */
  setCampaignColors(colors: string[]): void {
    if (colors && colors.length > 0) {
      this.campaignColors = colors;
    }
  }

  /**
   * Trigger confetti with custom or preset configuration
   * @param config Confetti configuration options
   */
  trigger(config: ConfettiConfig = {}): void {
    console.log('[ConfettiService] trigger() called with config:', config);
    
    const defaults = config.preset ? this.getPresetDefaults(config.preset) : {};
    const colors = config.colors || this.campaignColors;

    const finalConfig = {
      ...defaults,
      ...config,
      colors
    };

    // Remove preset from final config (not a confetti option)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (finalConfig as any).preset;

    console.log('[ConfettiService] Calling canvas-confetti with config:', finalConfig);
    confetti(finalConfig);
  }

  /**
   * Get default configuration for preset
   */
  private getPresetDefaults(preset: string): Partial<confetti.Options> {
    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    switch (preset) {
      case 'win':
        return {
          angle: randomInRange(55, 125),
          spread: randomInRange(50, 70),
          particleCount: randomInRange(150, 300),
          origin: { y: 0.6 }
        };
      
      case 'redeem':
        return {
          particleCount: 150,
          spread: 80,
          origin: { y: 0.7 }
        };
      
      case 'celebration':
        return {
          particleCount: 300,
          spread: 120,
          origin: { y: 0.5 }
        };
      
      default:
        return {
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        };
    }
  }

  /**
   * Convenience method: Trigger win celebration confetti
   * Uses same animation pattern as Experience app wheel component
   */
  win(): void {
    console.log('[ConfettiService] win() called');
    
    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    // Fire two bursts with campaign colors (matches Experience app wheel)
    const colors = this.campaignColors || ['#ed1f24', '#545454'];
    
    // First burst - with high z-index to appear above modals
    confetti({
      angle: randomInRange(55, 125),
      spread: randomInRange(50, 70),
      particleCount: randomInRange(150, 300),
      origin: { y: 0.6 },
      colors: [colors[0] || '#ed1f24'],
      zIndex: 10000 // Above Angular Material overlay (1000)
    });

    // Second burst with different color - also with high z-index
    confetti({
      angle: randomInRange(55, 125),
      spread: randomInRange(50, 70),
      particleCount: randomInRange(150, 300),
      origin: { y: 0.6 },
      colors: [colors[1] || '#545454'],
      zIndex: 10000 // Above Angular Material overlay (1000)
    });
  }

  /**
   * Convenience method: Trigger redeem confetti
   * Creates continuous falling gold squares for specified duration
   * @param seconds Duration in seconds (0 = indefinite)
   */
  redeem(seconds = 15): void {
    console.log('[ConfettiService] redeem() called with duration:', seconds);
    
    const duration = seconds * 1000;
    const animationEnd = seconds === 0 ? 0 : Date.now() + duration;
    let skew = 1;

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const frame = () => {
      const timeLeft = animationEnd === 0 ? 1000 : animationEnd - Date.now();
      const ticks = animationEnd === 0 ? 200 : Math.max(200, 500 * (timeLeft / duration));
      skew = Math.max(0.8, skew - 0.001);

      confetti({
        particleCount: 1,
        startVelocity: 0,
        ticks: ticks,
        origin: {
          x: Math.random(),
          // since particles fall down, skew start toward the top
          y: (Math.random() * skew) - 0.2
        },
        colors: ['#FFD700'],
        shapes: ['square'],
        gravity: randomInRange(0.4, 0.6),
        scalar: randomInRange(0.4, 1),
        drift: randomInRange(-0.4, 0.4),
        zIndex: 10000 // Above Angular Material overlay (1000)
      });

      if (animationEnd === 0 || timeLeft > 0) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }

  /**
   * Convenience method: Trigger general celebration confetti
   */
  celebration(): void {
    this.trigger({ preset: 'celebration' });
  }
}
